import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    effect,
    forwardRef,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import {
    ZardFormFieldComponent,
    ZardFormControlComponent,
    ZardFormLabelComponent,
    ZardFormMessageComponent,
} from '@/styles/components/form/form.component'
import { mergeClasses, noopFn } from '@/styles/utils/merge-classes'
import type { ClassValue } from 'clsx'

let notesTextareaFieldIdSeq = 0

/** ~full-width textarea at typical form width; used only to cap resize height from maxLength */
const APPROX_CHARS_PER_LINE = 44
const LINE_HEIGHT_PX = 20
/** vertical padding (2×12px) + horizontal border (2×2px) on the control */
const TEXTAREA_VERTICAL_CHROME_PX = 28

@Component({
    selector: 'app-notes-textarea-field',
    standalone: true,
    imports: [
        ZardFormFieldComponent,
        ZardFormControlComponent,
        ZardFormLabelComponent,
        ZardFormMessageComponent,
    ],
    templateUrl: './notes-textarea-field.component.html',
    styleUrl: './notes-textarea-field.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NotesTextareaFieldComponent),
            multi: true,
        },
    ],
    host: {
        class: 'notes-textarea-field-host',
    },
})
export class NotesTextareaFieldComponent implements ControlValueAccessor {
    private readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl')

    readonly label = input<string>('')
    readonly placeholder = input<string>('')
    readonly rows = input<number>(4)
    readonly maxLength = input<number | null>(null)
    readonly showCounter = input(false)
    readonly requiredAsterisk = input(false)
    readonly minLengthWarning = input<number | null>(null)
    readonly fieldClass = input<ClassValue>('')
    readonly textareaClass = input<ClassValue>('')
    readonly textareaExtraClass = input<ClassValue>('')
    readonly errorMessage = input<string | null>(null)
    readonly fieldId = input<string | undefined>(undefined)
    /** Merges with CVA disabled (e.g. template-driven read-only like `!isEditing`). */
    readonly inputDisabled = input(false)

    readonly blurred = output<void>()
    readonly valueChanged = output<string>()

    protected readonly generatedId = `app-notes-textarea-${++notesTextareaFieldIdSeq}`
    protected readonly controlId = computed(() => this.fieldId() ?? this.generatedId)

    protected readonly value = signal('')
    protected readonly cvaDisabled = signal(false)

    protected readonly isDisabled = computed(() => this.cvaDisabled() || this.inputDisabled())

    protected readonly textareaClasses = computed(() =>
        mergeClasses(
            'notes-textarea-field__control',
            this.errorMessage() ? 'notes-textarea-field__control--error' : '',
            this.textareaClass(),
            this.textareaExtraClass(),
        ),
    )

    protected readonly charCount = computed(() => this.value().length)
    protected readonly highlightBelowMin = computed(() => {
        const min = this.minLengthWarning()
        if (min == null) {
            return false
        }
        const len = this.value().trim().length
        return len > 0 && len < min
    })

    protected readonly showFooterRow = computed(
        () =>
            !!this.errorMessage() ||
            (this.showCounter() && this.maxLength() != null && this.maxLength()! > 0),
    )

    protected readonly isHeightCapped = computed(() => {
        const max = this.maxLength()
        return max != null && max > 0
    })

    /** Pixel cap derived from maxLength (see {@link APPROX_CHARS_PER_LINE}). */
    protected readonly textareaHeightCapPx = computed((): number | null => {
        const max = this.maxLength()
        if (max == null || max <= 0) {
            return null
        }
        const lineCount = Math.max(this.rows(), Math.ceil(max / APPROX_CHARS_PER_LINE))
        return lineCount * LINE_HEIGHT_PX + TEXTAREA_VERTICAL_CHROME_PX
    })

    private onChange: (value: string) => void = (_value: string) => noopFn()
    private onTouched: () => void = noopFn

    constructor() {
        effect(() => {
            const value = this.value()
            const element = this.textareaRef()?.nativeElement
            if (!element) {
                return
            }
            if (element.value !== value) {
                element.value = value
            }
        })
    }

    writeValue(value: string | null): void {
        this.value.set(value ?? '')
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.cvaDisabled.set(isDisabled)
    }

    protected onInput(event: Event): void {
        const element = event.target as HTMLTextAreaElement
        const next = element.value
        this.value.set(next)
        this.onChange(next)
        this.valueChanged.emit(next)
    }

    protected onBlur(): void {
        this.onTouched()
        this.blurred.emit()
    }
}
